import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

/**
 * One feature-tile shown above an overview list (Numbers, …) so customers
 * can jump to a related sub-feature without us pushing extra items into
 * the sidebar. Visually consistent with the dashboard tiles: a brand-soft
 * coloured square holds the icon, followed by the title + subtitle.
 *
 * Each tile renders as a router <Link> so screen readers and keyboard
 * users get a real anchor to traverse with.
 */
export interface FeatureTileItem {
  to: string;
  label: string;
  description?: string;
  icon: ReactNode;
}

export function FeatureTiles({ items }: { items: FeatureTileItem[] }) {
  if (items.length === 0) return null;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
        gap: 12,
        marginBottom: 16,
      }}
    >
      {items.map((item) => (
        <FeatureTile key={item.to} item={item} />
      ))}
    </div>
  );
}

function FeatureTile({ item }: { item: FeatureTileItem }) {
  return (
    <Link
      to={item.to}
      className="tile tile-link"
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 14,
        padding: "14px 18px",
        textDecoration: "none",
        color: "inherit",
      }}
      aria-label={item.label}
    >
      <span
        aria-hidden="true"
        style={{
          width: 40,
          height: 40,
          borderRadius: 12,
          background: "var(--portal-primary-soft)",
          color: "var(--portal-primary-darker)",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
        }}
      >
        {item.icon}
      </span>
      <div style={{ minWidth: 0, flex: "1 1 0" }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: "1rem",
            color: "var(--portal-text)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {item.label}
        </div>
        {item.description && (
          <div
            style={{
              color: "var(--portal-muted)",
              fontSize: "0.85rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.description}
          </div>
        )}
      </div>
      <ChevronRight
        size={16}
        style={{ color: "var(--portal-primary)", flexShrink: 0 }}
      />
    </Link>
  );
}
